// Nuclear escape hatch. Used by the "Sign out" / "Reset" buttons when normal
// supabase.auth.signOut() can hang (stale session, network down, service-worker
// caching old bundle). Clears every form of client-side state and forces a
// reload that bypasses caches.
//
// Safe to call multiple times. Always navigates to "/" at the end.

export async function hardReset(): Promise<void> {
  // 1. Try a clean Supabase sign-out (best effort, 2s budget).
  try {
    const { supabase } = await import('./supabase')
    await Promise.race([
      supabase.auth.signOut().catch(() => {}),
      new Promise(res => setTimeout(res, 2000)),
    ])
  } catch {}

  // 2. Clear browser storage so the next load sees a clean slate.
  //    This also clears any saved deep link from a QR scan (ss.return_to).
  try { localStorage.clear() } catch {}
  try { sessionStorage.clear() } catch {}

  // 3. Drop IndexedDB databases (offline outbox lives here).
  try {
    const dbs = await (indexedDB as any).databases?.() || []
    await Promise.all(
      dbs.map((d: any) => new Promise<void>(res => {
        if (!d?.name) return res()
        const req = indexedDB.deleteDatabase(d.name)
        req.onsuccess = req.onerror = req.onblocked = () => res()
      })),
    )
  } catch {}

  // 4. Unregister every service worker so the next load fetches fresh JS.
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map(r => r.unregister().catch(() => false)))
    }
  } catch {}

  // 5. Nuke caches (PWA precache).
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k).catch(() => false)))
    }
  } catch {}

  // 6. Force a cache-busting reload to "/" so the user starts fresh.
  const url = new URL('/', window.location.origin)
  url.searchParams.set('r', String(Date.now()))
  window.location.replace(url.toString())
}
